import BreadCrumb from "Components/Common/BreadCrumb";
import PageTabs from "Components/Common/PageTabs";
import PageHeaderActions from "Components/Common/PageHeaderActions";
import { useEffect, useState } from "react";
import { Col, Container } from "reactstrap";
import QuoteSummary from "Components/Quotes/QuoteSummary";
import QuoteDescription from "Components/Quotes/QuoteDescription";
import QuoteSections from "Components/Quotes/QuoteSections";
import { useLocation, useNavigate } from "react-router-dom";
import { useSelector } from "react-redux";
import {
  deleteQuote,
  getDuplicateQuoteById,
  getQuoteById,
  getQuotePdfPreview,
} from "slices/quotes/thunk";
import {
  clearPdfPreview,
  clearQuoteDetails,
  selectQuotesState,
} from "slices/quotes/reducer";
import { createSelector } from "reselect";
import Loader from "Components/Common/Loader";
import ShareModal from "Components/Modals/ShareModal";
import PdfPreviewModal from "Components/Modals/PdfPreviewModal";
import Icon, { IconName } from "Components/Icons/Icon";
import { clearItemState } from "slices/items/reducer";
import DeleteItemModal from "Components/Modals/DeleteItemModal";
import { useAppDispatch, useAppSelector } from "Components/Hooks/hooks";
import { ErrorHandler, ErrorToast } from "utils/toast";
import OverlayGuide from "Components/Common/TourGuide/OverlayGuide";
import { useScreenTour } from "tour/useScreenTour";
import { tourElementsIds } from "tour/tourElementsIds";
import RestrictedAccessModal from "Components/Modals/RestrictedAccessModal";

const QuoteDetails = () => {
  const [tab, setTab] = useState("1");
  const [showShareModal, setShowShareModal] = useState(false);
  const [showPdfPreviewModal, setShowPdfPreviewModal] = useState(false);
  const [deleteQuoteModal, setDeleteQuoteModal] = useState<boolean>(false);
  const [showSubscriptionExpiredPopUp, setShowSubscriptionExpiredPopUp] =
    useState(false);

  const openShareModal = () => setShowShareModal(true);
  const closeShareModal = () => setShowShareModal(false);
  const [isLoadingDownload, setIsLoadingDownload] = useState<boolean>(false);
  const [isLoadingPreview, setIsLoadingPreview] = useState<boolean>(false);
  const { state } = useLocation();
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const quoteDetailsTour = useScreenTour("quoteDetails");
  const user = useAppSelector((state) => state.auth.user);

  const hasNotSubscribed =
    user?.is_subscription_active === false && user.is_trial_period === false;
  const hasBankAccount = user?.hasBankAccountDetailAdded;
  const hasSignature = user?.hasSignatureAdded;

  const [restrictionModalData, setRestrictionModalData] = useState<{
    iconName: IconName;
    title: string;
    description: string;
    buttonText: string;
    onAction: () => void;
  }>({
    iconName: "" as IconName,
    title: "",
    description: "",
    buttonText: "",
    onAction: () => {},
  });

  const RESTRICTION_CONFIG = {
    SUBSCRIPTION_REQUIRED: {
      iconName: "SubscriptionRequired",
      title: "Subscription Required",
      description:
        "To continue using EaziQuote, please activate or renew your subscription.",
      buttonText: "Activate Subscription",
      onAction: () => {
        if (user?.is_team_member) {
          ErrorToast(
            "Only the account owner can manage subscriptions. Please contact your owner.",
          );
        } else {
          navigate("/subscribe-plan");
        }
      },
    },
    BANK_ACCOUNT_REQUIRED: {
      iconName: "Info",
      title: "Add Bank Details",
      description:
        "Including bank details makes it easier for clients to pay you without follow-ups.",
      buttonText: "Add Bank Details",
      onAction: () => navigate("/payments-and-invoicing"),
    },
    SIGNATURE_REQUIRED: {
      iconName: "Info",
      title: "Add Your Signature",
      description:
        "Adding a signature helps build trust and makes your quote feel complete and professional.",
      buttonText: "Add Signature",
      onAction: () =>
        navigate("/payments-and-invoicing", {
          state: {
            signatureMissing: true,
          },
        }),
    },
    BANK_AND_SIGNATURE_REQUIRED: {
      iconName: "Info",
      title: "Complete Your Setup",
      description:
        "Add your bank details and signature to ensure your quotes look professional and include payment information.",
      buttonText: "Complete Setup",
      onAction: () => navigate("/payments-and-invoicing"),
    },
  } as const;

  const quoteSelector = createSelector(selectQuotesState, (state) => ({
    quoteDetails: state.quoteDetails,
    isLoading: state.status.fetchById,
    error: state.errors.fetchById,
    pdfPreviewHtml: state.pdfPreviewHtml,
  }));

  const { quoteDetails, isLoading, error, pdfPreviewHtml } =
    useSelector(quoteSelector);

  const closePdfPreviewModal = () => {
    setShowPdfPreviewModal(false);
    dispatch(clearPdfPreview());
  };

  useEffect(() => {
    if (state?.id) {
      dispatch(getQuoteById(state.id));
    } else {
      navigate(-1);
    }
  }, [dispatch, navigate, state]);

  if (isLoading === "loading" || !quoteDetails) {
    return (
      <Container
        className="d-flex justify-content-center align-items-center"
        style={{ minHeight: "100vh" }}
      >
        <Loader error={error} />
      </Container>
    );
  }

  const handleDownload = async () => {
    try {
      setIsLoadingDownload(true);

      // Create link and trigger download
      let downloadUrl = `${quoteDetails?.url}?download=1`;
      const link = document.createElement("a");
      link.href = downloadUrl;
      link.download = `${quoteDetails.reference_number}.pdf`;
      link.click();

      // Cleanup
      URL.revokeObjectURL(downloadUrl);
      link.remove();
    } catch (error) {
      ErrorHandler(error);
    } finally {
      setIsLoadingDownload(false);
    }
  };

  const handlePreview = async () => {
    try {
      setIsLoadingPreview(true);
      await dispatch(getQuotePdfPreview(quoteDetails.id)).unwrap();
      setShowPdfPreviewModal(true);
    } catch (error) {
      ErrorHandler(error);
    } finally {
      setIsLoadingPreview(false);
    }
  };

  const navigateToInvoice = () => {
    if (
      user?.is_subscription_active === false &&
      user.is_trial_period === false
    ) {
      setShowSubscriptionExpiredPopUp(true);
      setRestrictionModalData(RESTRICTION_CONFIG.SUBSCRIPTION_REQUIRED);
    } else {
      navigate("/manage-invoice", {
        state: {
          quoteId: quoteDetails.id,
          reference_number: quoteDetails.reference_number,
        },
      });
    }
  };

  const duplicateQuote = async () => {
    if (hasNotSubscribed) {
      setShowSubscriptionExpiredPopUp(true);
      setRestrictionModalData(RESTRICTION_CONFIG.SUBSCRIPTION_REQUIRED);
    } else if (!hasBankAccount && !hasSignature) {
      setShowSubscriptionExpiredPopUp(true);
      setRestrictionModalData(RESTRICTION_CONFIG.BANK_AND_SIGNATURE_REQUIRED);
    } else if (!hasBankAccount) {
      setShowSubscriptionExpiredPopUp(true);
      setRestrictionModalData(RESTRICTION_CONFIG.BANK_ACCOUNT_REQUIRED);
    } else if (!hasSignature) {
      setShowSubscriptionExpiredPopUp(true);
      setRestrictionModalData(RESTRICTION_CONFIG.SIGNATURE_REQUIRED);
    } else {
      try {
        await dispatch(
          getDuplicateQuoteById({
            quote_id: quoteDetails.id,
          }),
        )?.unwrap();
        if (isLoading === "succeeded") {
          navigate(-1);
        }
      } catch (error) {
        console.log("Error duplicating quote:", error);
      }
    }
  };

  const moreActionGroups = [
    quoteDetails.is_editable
      ? [
          {
            label: "Edit",
            icon: <Icon name="Edit" />,
            onClick: () => {
              if (hasNotSubscribed) {
                setRestrictionModalData(
                  RESTRICTION_CONFIG.SUBSCRIPTION_REQUIRED,
                );
                setShowSubscriptionExpiredPopUp(true);
              } else if (!hasBankAccount && !hasSignature) {
                setShowSubscriptionExpiredPopUp(true);
                setRestrictionModalData(
                  RESTRICTION_CONFIG.BANK_AND_SIGNATURE_REQUIRED,
                );
              } else if (!hasBankAccount) {
                setShowSubscriptionExpiredPopUp(true);
                setRestrictionModalData(
                  RESTRICTION_CONFIG.BANK_ACCOUNT_REQUIRED,
                );
              } else if (!hasSignature) {
                setShowSubscriptionExpiredPopUp(true);
                setRestrictionModalData(RESTRICTION_CONFIG.SIGNATURE_REQUIRED);
              } else {
                dispatch(clearQuoteDetails());
                dispatch(clearItemState());
                navigate("/manage-quotes", {
                  state: { id: quoteDetails.id },
                });
              }
            },
          },
          {
            label: "Duplicate",
            icon: <Icon name="Duplicate" />,
            onClick: duplicateQuote,
          },
          {
            label: "Delete",
            icon: <Icon name="Delete" />,
            onClick: () => {
              setDeleteQuoteModal(true);
            },
          },
        ]
      : [
          {
            label: "Duplicate",
            icon: <Icon name="Duplicate" />,
            onClick: duplicateQuote,
          },
        ],
  ];

  const shareExportsGroups = [
    [
      {
        label: "Download",
        icon: <Icon name="Download" />,
        onClick: handleDownload,
      },
      {
        label: "Share",
        icon: <Icon name="BlackShare" />,
        onClick: openShareModal,
      },
    ],
  ];

  return (
    <>
      <div className="page-content" id={tourElementsIds.quoteDetailsTour}>
        <Container fluid>
          <BreadCrumb title="Quote Detail" pageTitle="Quotes" url="/quotes" />
          <div className="gap-4 d-flex flex-column">
            <PageHeaderActions
              title={quoteDetails.title}
              subtitle={quoteDetails.reference_number}
              actions={[
                ...(quoteDetails?.status === "approved"
                  ? [
                      {
                        label: "Invoice",
                        onClick: navigateToInvoice,
                        leftIcon: (
                          <i className="ri-add-fill me-1 align-bottom"></i>
                        ),
                        type: "button" as const,
                      },
                    ]
                  : []),
                ...(quoteDetails?.status === "draft"
                  ? []
                  : [
                      {
                        label: "Preview",
                        onClick: handlePreview,
                        leftIcon: <Icon name="Pause" />,
                        isLoading: isLoadingPreview,
                      },
                    ]),
                {
                  type: "dropdown",
                  label: "More Actions",
                  groups: moreActionGroups,
                },
                ...(quoteDetails?.status === "draft"
                  ? []
                  : [
                      {
                        type: "dropdown" as const,
                        label: "Share & Export",
                        groups: shareExportsGroups,
                        isLoading: isLoadingDownload,
                      },
                    ]),
              ]}
            />

            <Col xxl={12}>
              <PageTabs
                active={tab}
                onChange={setTab}
                tabs={[
                  { id: "1", label: "Summary" },
                  { id: "2", label: "Description" },
                  { id: "3", label: "Sections" },
                ]}
              />
              {tab === "1" && <QuoteSummary quote={quoteDetails} />}
              {tab === "2" && <QuoteDescription quote={quoteDetails} />}
              {tab === "3" && <QuoteSections quoteDetails={quoteDetails} />}
            </Col>
          </div>
        </Container>
      </div>
      <ShareModal
        isOpen={showShareModal}
        onClose={closeShareModal}
        pdfTitle={quoteDetails.reference_number}
        route_url={quoteDetails?.route_url}
        pdfUrl={quoteDetails?.url}
        isInvoice={false}
        quoteDetails={quoteDetails}
        isShareFromQuote
      />
      <PdfPreviewModal
        isOpen={showPdfPreviewModal}
        onClose={closePdfPreviewModal}
        html={pdfPreviewHtml || ""}
      />

      <DeleteItemModal
        show={deleteQuoteModal}
        onDeleteClick={() => {
          dispatch(deleteQuote(quoteDetails.id));
          setDeleteQuoteModal(false);
          navigate(-1);
        }}
        onCloseClick={() => {
          setDeleteQuoteModal(false);
        }}
      />

      <OverlayGuide {...quoteDetailsTour} />
      <RestrictedAccessModal
        isOpen={showSubscriptionExpiredPopUp}
        onCancel={() => setShowSubscriptionExpiredPopUp(false)}
        iconName={restrictionModalData.iconName}
        title={restrictionModalData.title}
        description={restrictionModalData.description}
        buttonText={restrictionModalData.buttonText}
        onButtonClick={restrictionModalData.onAction}
      />
    </>
  );
};

export default QuoteDetails;
